import { getUid } from 'ol/util'
import { assert } from 'ol/asserts';
import Cluster from 'ol/source/Cluster'
import { buffer, createEmpty, createOrUpdateFromCoordinate } from 'ol/extent'
import {add as addCoordinate, scale as scaleCoordinate} from 'ol/coordinate';
import { getCenter } from 'ol/extent';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';

class CityhubCluster extends Cluster
{
    constructor(options) {
        options.geometryFunction = function (feature) {
            const geometryTypes = ['Point', 'LineString', 'Polygon', 'Circle'];
            const geometry = feature.getGeometry();
            assert(
                !geometry || geometryTypes.includes(geometry.getType()),
                `The default 'geometryFunction' can only handle '${geometryTypes.join(', ')}' or null geometries`,
            );
            return geometry;
        };

        super(options);

        this.filters = options.filters;
        this.isValid = options.filterFunc || function() { return true; };

        /**
         * @private
         * @type {import("../PluggableMap.js").default}
         */
        this._map = options.map || null;

        // Whether the default behaviour of clicking on a cluster is to zoom into it.
        // this.zoomToBoundsOnClick = options.zoomToBoundsOnClick || true;
        // When you click a cluster at the bottom zoom level we spiderfy it so you can see all of its markers.
        // this.spiderfyOnMaxZoom = options.spiderfyOnMaxZoom || true;
        //If set, at this zoom level and below, markers will not be clustered.
        this.disableClusteringAtZoom = options.disableClusteringAtZoom || null;
    }

    setFilters(filters) {
        this.filters = filters;
    }

    /**
    * @protected
    */
    cluster() {
        if (this.resolution === undefined || !this.source) {
          return;
        }
        let mapView = this._map.getView();
        var currrentZoom = this._map ? Math.ceil(mapView.getZoom()) : null,
            maxZoom = this._map ? Math.ceil(mapView.getMaxZoom()) : null,
            minZoom = this._map ? Math.floor(mapView.getMinZoom()) : null;

        if (this.disableClusteringAtZoom !== null) {
            maxZoom = this.disableClusteringAtZoom - 1;
        }

        const extent = createEmpty();
        const mapDistance = this.disableClusteringAtZoom !== null && currrentZoom >= maxZoom ? 0 : this.distance * this.resolution;
        const features = this.source.getFeatures();

        /** @type {Object<string, true>} */
        const clustered = {};

        for (let i = 0, ii = features.length; i < ii; i++) {
            const feature = features[i];
            if (!(getUid(feature) in clustered)) {
                const geometry = this.geometryFunction(feature)
                if (geometry && this.isValid(feature, this.filters)) {
                    let neighbors = [];
                    if (mapDistance > 0){
                        const coordinates = getCenter(geometry.getExtent());
                        createOrUpdateFromCoordinate(coordinates, extent)
                        buffer(extent, mapDistance, extent)

                        neighbors = this.source
                            .getFeaturesInExtent(extent)
                            .filter((neighbor) => {
                                if(!this.isValid(neighbor, this.filters)) {
                                    return false
                                }

                                const uid = getUid(neighbor);
                                if (uid in clustered) {
                                    return false;
                                }
                                clustered[uid] = true;
                                return true;
                            });
                    }else{
                        neighbors = [feature];
                    }

                    this.features.push(this.createCluster(neighbors, extent));
                }
            }
        }
    }

    createCluster(features, extent) {
        const centroid = [0, 0];
        const size = features.length;
        for (let i = size - 1; i >= 0; --i) {
            const geometry = this.geometryFunction(features[i]);
            if (geometry) {
                addCoordinate(centroid, getCenter(geometry.getExtent()));
            } else {
                features.splice(i, 1);
            }
        }
        scaleCoordinate(centroid, 1 / features.length);
        const searchCenter = getCenter(extent);
        const ratio = this.interpolationRatio;
        const geometry = size > 1 ? new Point([
            centroid[0] * (1 - ratio) + searchCenter[0] * ratio,
            centroid[1] * (1 - ratio) + searchCenter[1] * ratio,
        ]) : features[0].getGeometry();

        if (this.createCustomCluster_) {
            return this.createCustomCluster_(geometry, features);
        }
        return new Feature({
            geometry,
            features,
        });
    }

}

export default CityhubCluster;
